import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { waitlistApi, type AdminWaitlistEntry } from '../../services/waitlistApi';

interface SessionMeta {
  id: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  currentBookings: number;
  isCancelled: boolean;
  activity: { id: string; slug: string; titleCN: string | null; titleEN: string | null };
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AdminSessionWaitlist() {
  const siteLanguage = useSiteLanguage();
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [session, setSession] = useState<SessionMeta | null>(null);
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const load = useCallback(async () => {
    if (!isAuthenticated || !sessionId) return;
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('No auth token');
      const result = await waitlistApi.adminListForSession(token, sessionId);
      if (!result) {
        setSession(null);
        setEntries([]);
        return;
      }
      setSession({
        ...result.session,
        startTime: String(result.session.startTime),
        endTime: String(result.session.endTime),
      });
      setEntries(result.entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, sessionId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading || loading) {
    return (
      <AdminLayout title="Session Waitlist">
        <div className="text-gray-500">Loading…</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Session Waitlist">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">
          {error}
        </div>
      </AdminLayout>
    );
  }

  if (!session) {
    return (
      <AdminLayout title="Session Waitlist">
        <div className="text-gray-500">Session not found.</div>
      </AdminLayout>
    );
  }

  const activityTitle = localize(session.activity.titleCN, session.activity.titleEN, siteLanguage);

  return (
    <AdminLayout title="Session Waitlist">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link
              to={`/admin/activities/${session.activity.id}/edit`}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to activity
            </Link>
            <h2 className="text-xl font-semibold text-gray-900 mt-2">{activityTitle}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateTime(session.startTime)} – {formatDateTime(session.endTime)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Capacity: {session.currentBookings} / {session.maxCapacity}
              {session.isCancelled && (
                <span className="ml-2 text-red-600 font-medium">(Session cancelled)</span>
              )}
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary">{entries.length}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">on waitlist</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {entries.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No one is on the waitlist for this session.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Children</th>
                  <th className="px-4 py-3 text-left">Adults</th>
                  <th className="px-4 py-3 text-left">Demand</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{entry.position + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{entry.user.name || '—'}</div>
                      <div className="text-xs text-gray-500">{entry.user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{entry.childrenCount}</td>
                    <td className="px-4 py-3 text-gray-700">{entry.adultsCount}</td>
                    <td className="px-4 py-3 text-gray-700">{entry.capacityDemand}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={entry.status} />
                      {entry.status === 'NOTIFIED' && entry.notifiedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDateTime(entry.notifiedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatDateTime(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string }> = {
    WAITING: { bg: 'bg-blue-100', text: 'text-blue-800' },
    NOTIFIED: { bg: 'bg-amber-100', text: 'text-amber-800' },
    EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-700' },
  };
  const c = config[status] || config.WAITING;
  return (
    <span
      className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}
    >
      {status}
    </span>
  );
}
